import { FungibleConditionCode, principalCV, someCV, uintCV } from '@stacks/transactions'
import { STACKS_NETWORK, STACKS_NETWORK_TYPE } from '../../config/stacks'
import { ALEX_CONTRACT_ADDRESSES, ALEX_CONTRACT_NAMES, ALEX_FUNCTION_NAMES, UWU_CONTRACT_ADDRESSES } from '../../constants/smartContracts'
import { ProtocolName } from '../../enums/protocols'
import { LPToken, Token } from '../../interfaces/tokens'
import { readOnlyRetry } from '../../utils/fetchUtils'
import { formatTokenValue, formatValue } from '../../utils/formattingUtils'
import { SwapTransaction } from '../../interfaces/transactions'
import { User } from '../../interfaces/user'
import { useCallback } from 'react'
import { getTokenContract } from '../../utils/tokenUtils'
import { request } from '@stacks/connect'
import { createContractPostCondition, createUserPostCondition } from '../../utils/postConditionUtils'
import { swapEnabledTokens } from '../../constants/tokens'
import { SwapAmounts } from '../../interfaces/swap'

export const getAlexSwapAmounts = async (lpToken: LPToken, inputToken: Token, outputToken: Token, amount: number, slippageTolerance: number): Promise<SwapAmounts> => {
  if (isNaN(amount) || amount === 0 || amount === null || !swapEnabledTokens.includes(inputToken.name) || !swapEnabledTokens.includes(outputToken.name) || lpToken.protocol !== ProtocolName.ALEX || !lpToken.enabled) {
    return {
      amount: amount,
      est: {
        formatted: 0,
        unformatted: 0
      },
      min: {
        formatted: 0,
        unformatted: 0
      },
      swapFee: lpToken.fee,
      priceImpact: 0,
      slippageTolerance: slippageTolerance
    }
  }

  const inputTokenContractDetails = inputToken.contract[STACKS_NETWORK_TYPE]
  const outputTokenContractDetails = outputToken.contract[STACKS_NETWORK_TYPE]

  if (!inputTokenContractDetails?.address || !inputTokenContractDetails?.name) {
    throw new Error(`Contract details for Token '${inputToken.name}' are missing`)
  }

  if (!outputTokenContractDetails?.address || !outputTokenContractDetails?.name) {
    throw new Error(`Contract details for Token '${outputToken.name}' are missing`)
  }

  if (!lpToken?.alexOptions) {
    throw new Error(`Required options for protocol '${ProtocolName.ALEX}' are missing from LP Token '${lpToken.name}'`)
  }

  const xTokenContract = getTokenContract(inputToken, STACKS_NETWORK_TYPE, true, ProtocolName.ALEX)!
  const yTokenContract = getTokenContract(outputToken, STACKS_NETWORK_TYPE, true, ProtocolName.ALEX)!

  const poolFactor = lpToken.alexOptions.poolFactor
  const tokenFactor = lpToken.alexOptions.tokenFactor

  const fee = (amount * lpToken.fee)
  const scaledAmount = formatValue((amount - fee) * tokenFactor, 0)

  const response = await readOnlyRetry({
    contractAddress: ALEX_CONTRACT_ADDRESSES.DEPLOYER,
    contractName: ALEX_CONTRACT_NAMES.POOL,
    functionName: ALEX_FUNCTION_NAMES.GET_HELPER,
    functionArgs: [
      principalCV(xTokenContract),
      principalCV(yTokenContract),
      uintCV(poolFactor),
      uintCV(scaledAmount)
    ],
    network: STACKS_NETWORK,
    senderAddress: UWU_CONTRACT_ADDRESSES.DEPLOYER,
  })

  if (response?.value?.value === null) {
    throw new Error(`Failed to fetch estimated amount received using LP Token '${lpToken.name}'`)
  }

  const formattedResponse = formatValue(response.value.value)

  const estReceived = formatValue(formattedResponse / (tokenFactor / outputToken.decimals), 0)
  const minReceived = formatValue(estReceived * (1 - slippageTolerance), 0)

  const formattedEstReceived = formatTokenValue(estReceived, outputToken)
  const formattedMinReceived = formatTokenValue(minReceived, outputToken)

  return {
    amount: amount,
    est: {
      formatted: formattedEstReceived,
      unformatted: estReceived
    },
    min: {
      formatted: formattedMinReceived,
      unformatted: minReceived
    },
    swapFee: lpToken.fee,
    priceImpact: 0,
    slippageTolerance: slippageTolerance
  }
}

export const useAlexSwap = () => {
  const handleAlexSwap = useCallback(
    async (user: User, options: SwapTransaction) => {
      const inputToken = options.tokens.input
      const outputToken = options.tokens.output
      const lpToken = options.tokens.lp

      if (!lpToken) {
        throw new Error(`No valid LP Token was provided for protocol '${ProtocolName.ALEX}'`)
      }

      const inputTokenContractDetails = inputToken.contract[STACKS_NETWORK_TYPE]
      const outputTokenContractDetails = outputToken.contract[STACKS_NETWORK_TYPE]

      if (lpToken.protocol !== ProtocolName.ALEX) {
        throw new Error(`Protocol used by LP Token '${lpToken.name}' doesn't match protocol '${ProtocolName.ALEX}'`)
      }

      if (!inputTokenContractDetails?.address || !inputTokenContractDetails?.name) {
        throw new Error(`Contract details for Token '${inputToken.name}' are missing`)
      }

      if (!outputTokenContractDetails?.address || !outputTokenContractDetails?.name) {
        throw new Error(`Contract details for Token '${outputToken.name}' are missing`)
      }

      if (!lpToken?.alexOptions) {
        throw new Error(`Required options for protocol '${ProtocolName.ALEX}' are missing from LP Token '${lpToken.name}'`)
      }

      const xTokenContract = getTokenContract(inputToken, STACKS_NETWORK_TYPE, true, ProtocolName.ALEX)!
      const yTokenContract = getTokenContract(outputToken, STACKS_NETWORK_TYPE, true, ProtocolName.ALEX)!

      const poolFactor = lpToken.alexOptions.poolFactor
      const tokenFactor = lpToken.alexOptions.tokenFactor

      const scaledAmountPostCondition = formatValue(options.amounts.amount * inputToken.decimals, 0)
      const minReceivedPostCondition = options.amounts.min.unformatted

      const scaledAmountFunctionArg = formatValue(options.amounts.amount * tokenFactor, 0)
      const minReceivedFunctionArg = formatValue(options.amounts.min.unformatted * (tokenFactor / outputToken.decimals), 0)

      const postConditions = [
        createUserPostCondition(
          user?.address,
          FungibleConditionCode.Equal,
          scaledAmountPostCondition,
          inputToken
        ),
        createContractPostCondition(
          ALEX_CONTRACT_ADDRESSES.DEPLOYER,
          ALEX_CONTRACT_NAMES.VAULT,
          FungibleConditionCode.GreaterEqual,
          minReceivedPostCondition,
          outputToken
        )
      ]
      
      const tx = await request('stx_callContract', {
        contract: `${ALEX_CONTRACT_ADDRESSES.DEPLOYER}.${ALEX_CONTRACT_NAMES.POOL}`,
        functionName: ALEX_FUNCTION_NAMES.SWAP_HELPER,
        functionArgs: [
          principalCV(xTokenContract),
          principalCV(yTokenContract),
          uintCV(poolFactor),
          uintCV(scaledAmountFunctionArg),
          someCV(uintCV(minReceivedFunctionArg))
        ],
        postConditionMode: 'deny',
        postConditions: postConditions,
        network: STACKS_NETWORK,
      })

      return tx
    },
    []
  )

  return { handleAlexSwap }
}